import {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type MutableRefObject,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { Paperclip, Square, ArrowUp, Mic, MicOff, ListPlus } from 'lucide-react';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { HotkeyHint } from '@reflex/ui/components/shared/Kbd';
import { useAltKeyHeld } from '@reflex/ui/hooks/useAltKeyHeld';
import { FOCUSABLE_SELECTOR } from '@reflex/ui/lib/focus-order';
import { Button } from '@reflex/ui/components/ui/button';
import { AttachmentChip } from '@reflex/ui/components/shared/AttachmentChip';
import { ImageLightbox } from '@reflex/ui/components/content/ImagePreview';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { cn } from '@reflex/ui/lib/utils';
import { useFileAttachments } from '../hooks/useFileAttachments';
import { useSpeechRecognition, voiceInputErrorMessage } from '../hooks/useSpeechRecognition';
import { useBlockRefresh } from '../useBlockRefresh';
import { CommandPicker } from './CommandPicker';
import {
  MentionEditor,
  type MentionEditorHandle,
  type MentionQuery,
} from './composer/MentionEditor';
import type { MentionData } from './composer/MentionNode';
import type { AvailableCommand } from './sdk-chat/types.js';
import { formatBytes } from '@runloop/reflex-contract';
import type { Attachment } from '@runloop/reflex-contract';
import type { AgentActivity } from '@reflex/shared';

/**
 * Imperative API a mention menu exposes so the composer can drive keyboard
 * navigation while the textarea keeps focus (Cursor-style). `selectHighlighted`
 * returns `true` when it applied a highlighted item, so the caller knows
 * whether to consume the Enter/Tab keypress (vs. letting it submit).
 */
export interface MentionMenuHandle {
  moveUp: () => void;
  moveDown: () => void;
  selectHighlighted: () => boolean;
  /**
   * Called when the user presses Escape. Return `true` if the menu consumed it
   * (e.g. navigated back from a sub-category to the top-level list) so the
   * composer keeps the menu open; return `false`/omit to let the composer close
   * the menu as usual.
   */
  escape?: () => boolean;
}

/** An active `@`-mention token detected at the caret. */
interface MentionToken {
  /**
   * Text typed after `@`, up to the caret. May contain spaces (multi-word
   * titles) but never starts with a space and never contains a second `@`.
   */
  query: string;
  /** Index of the `@` character in the textarea value. */
  start: number;
}

/**
 * Detect an active `@`-mention token at the caret. The token starts at an `@`
 * that is at the start of the text or preceded by whitespace, and runs up to
 * the caret. The query may contain spaces (multi-word titles) but never starts
 * with a space and never contains a second `@` (so `foo@bar` emails and
 * mid-word `@`s don't trigger it). Returns the query and the `@` index, or
 * `null` when the caret is not inside a token.
 */
export function detectMentionToken(text: string, caret: number): MentionToken | null {
  const before = text.slice(0, caret);
  const match = before.match(/(?:^|\s)@(|[^\s@\n][^@\n]*)$/);
  if (!match) return null;
  const query = match[1];
  return { query, start: caret - query.length - 1 };
}

/**
 * Filter slash commands to those whose name contains `query` (case-insensitive)
 * and rank them so Enter picks the closest match: an exact name first, then
 * prefix matches, then other substring matches. Ties keep their input order.
 */
export function rankSlashCommands(
  commands: readonly AvailableCommand[],
  query: string,
): AvailableCommand[] {
  const q = query.toLowerCase();
  return commands
    .map((cmd) => {
      const name = cmd.name.toLowerCase();
      const rank = name === q ? 0 : name.startsWith(q) ? 1 : name.includes(q) ? 2 : -1;
      return { cmd, rank };
    })
    .filter(({ rank }) => rank >= 0)
    .sort((a, b) => a.rank - b.rank)
    .map(({ cmd }) => cmd);
}

/** Debounce before auto-closing a space-containing empty mention menu. */
const MENTION_EMPTY_CLOSE_MS = 250;

function getChatInputPlaceholder({
  isExternallyDriven,
  devboxShutDown,
  agentActivity,
  isGenerating,
  canRedirect,
}: {
  isExternallyDriven: boolean;
  devboxShutDown: boolean;
  agentActivity?: AgentActivity;
  isGenerating?: boolean;
  canRedirect: boolean;
}): string {
  if (isExternallyDriven) return 'Replaying...';
  if (devboxShutDown) {
    return 'Devbox shut down. This session can’t be resumed. Start a new session.';
  }
  if (agentActivity === 'waiting_input') return 'Agent is waiting for your input...';
  if (agentActivity === 'interrupted' || agentActivity === 'completed') {
    return 'Send a message to continue...';
  }
  if (agentActivity === 'stopped') return 'Agent has finished';
  if (!isGenerating) return 'Type a message...';
  if (canRedirect) return 'Queue a message, or Cmd/Ctrl+Enter to redirect...';
  return 'Type a message (will be queued)...';
}

export interface ChatInputConfig {
  compact?: boolean;
  /**
   * The parent owns the horizontal gutter. Set this when the composer sits in a
   * column shared with other chat surfaces (the transcript, the queued-messages
   * slot) so its box lines up with theirs instead of insetting again.
   */
  flush?: boolean;
  submitLabel?: string;
  autoFocus?: boolean;
  initialValue?: string;
  initialAttachments?: Attachment[];
  /** When set (including empty string), the textarea is read-only and driven externally. */
  chatText?: string | null;
  /**
   * When true, the input value and attachments are preserved after submit
   * instead of being cleared. Useful for surfaces like guided demos where
   * the parent needs to display the submitted prompt after the form collapses.
   */
  preserveOnSubmit?: boolean;
}

interface ChatInputProps extends ChatInputConfig {
  onSend: (message: string, attachments: Attachment[]) => void;
  /**
   * Optional "redirect" action for a running agent. When provided and the agent
   * is generating, the composer offers a second submit affordance that
   * interrupts the current turn and sends the message immediately, instead of
   * queuing it behind the in-flight turn. Triggered by the Redirect button or
   * Cmd/Ctrl+Enter. Omit it (the default) to keep the plain queue-on-Enter
   * behavior for surfaces that don't support redirecting.
   */
  onRedirect?: (message: string, attachments: Attachment[]) => void;
  onStop?: () => void;
  /**
   * Called when the user presses Escape and no submenu (mention menu,
   * command picker) is open and a turn isn't generating. Return early or
   * omit to let Escape bubble: when no host owns the key, the composer
   * stops the event only when it actually has something to do (interrupt a
   * generating turn, or call this hook). Otherwise Escape falls through to
   * a wrapping surface — e.g. the Labs takeover's `esc`-back, or a Radix
   * Dialog's close — instead of stranding the user inside the composer.
   */
  onEscape?: () => void;
  /**
   * Fires whenever the internal text value changes (typing, speech, clear on
   * submit). Use this when the parent needs to observe the draft without
   * taking full control of the input (`chatText` does that instead).
   */
  onChange?: (value: string) => void;
  /**
   * Fires whenever the attachments array changes (add, remove, clear).
   */
  onAttachmentsChange?: (attachments: Attachment[]) => void;
  disabled?: boolean;
  isGenerating?: boolean;
  isPending?: boolean;
  agentActivity?: AgentActivity;
  /**
   * True when the agent's devbox went away underneath the session (for example,
   * from the Runloop dashboard or the retention policy). Kept as a boolean so
   * plugin-chat stays free of host dependencies.
   */
  devboxShutDown?: boolean;
  submitRef?: MutableRefObject<(() => void) | null>;
  commands?: AvailableCommand[];
  /**
   * Optional content rendered at the start (left) of the bottom toolbar row,
   * opposite the attach/mic/send controls. Used by launch surfaces to host the
   * agent settings chips and by a running agent's chat to show them read-only.
   * Kept a plain `ReactNode` so plugin-chat stays free of host dependencies.
   */
  leadingToolbar?: ReactNode;
  /**
   * Whether this composer's text reaches a backend that expands context
   * reference markers (the agent start / message paths). Only then does a
   * pasted item URL — a pull request link — become a context chip; elsewhere
   * the marker would reach the model as worse text than the URL it replaced.
   */
  contextReferences?: boolean;
  /**
   * Opt-in render prop for a Cursor-style `@`-mention menu. When provided,
   * typing `@` at the start of a word opens a menu positioned above the
   * composer; the textarea keeps focus so typing refines the query and the
   * composer forwards Arrow/Enter/Tab/Escape to the menu via `menuRef`. The
   * host supplies the menu node (e.g. the launch dialog's plugin-driven
   * `LaunchMentionMenu`), so plugin-chat stays free of host dependencies.
   */
  renderMentionMenu?: (args: {
    /** The mention query (text after `@`), trimmed. */
    query: string;
    /** Fixed-position style anchoring the menu above the composer. */
    style: React.CSSProperties;
    /** Close the menu (clears the active token's capture, keeps the text). */
    onClose: () => void;
    /** Remove the active `@token` text from the composer (call after applying). */
    clearMentionToken: () => void;
    /**
     * Replace the active `@token` with an inline, atomic context chip (e.g. an
     * agent-run reference). The chip moves and copies as one unit and
     * serializes back to its marker.
     */
    insertMention: (mention: MentionData) => void;
    /**
     * Clear the text typed after `@` while keeping the menu open (the `@`
     * stays, caret lands after it). Used to start a fresh narrower search when
     * the menu drills into or out of a category.
     */
    resetMentionQuery: () => void;
    /**
     * Replace the text after `@` with `text` (menu stays open, caret at end).
     * Used to scope the menu to a category by rewriting the token to
     * `@<keyword>:`.
     */
    setMentionQuery: (text: string) => void;
    /**
     * Report whether the menu currently has selectable rows. The composer
     * auto-closes after a multi-word query stays empty (debounced).
     */
    reportResults: (hasResults: boolean) => void;
    /** Imperative handle for keyboard nav: moveUp / moveDown / selectHighlighted. */
    menuRef: Ref<MentionMenuHandle>;
  }) => ReactNode;
  /**
   * For hosts that wrap the composer in a Radix dismissable layer (e.g. the
   * launch Dialog). Radix listens for Escape on `document` in the capture phase
   * and dismisses unless the event is `defaultPrevented`, which runs before the
   * composer's own keydown handler ever sees the key. The composer keeps this
   * ref pointed at a handler that consumes Escape when the mention menu is open
   * (stepping back one level, then closing the menu). The host calls it from
   * `onEscapeKeyDown` and `preventDefault()`s when it returns `true`, so Escape
   * ladders through the menu before it is allowed to close the dialog.
   */
  mentionEscapeRef?: MutableRefObject<(() => boolean) | null>;
  /**
   * Opens the `@`-mention menu programmatically, as if the user typed `@`.
   * Hosts point this at an external "+" trigger (e.g. the launch dialog's
   * "Add context" button that lives above the composer) so the button and the
   * `@` key share one code path. When the caret already sits in an `@`-token
   * the menu reopens on that token instead of inserting a second `@`. Only
   * wired when `renderMentionMenu` is set.
   */
  openMentionRef?: MutableRefObject<(() => void) | null>;
  /**
   * Whether the target model accepts image input. When `false`, image
   * attachments (paste, drop, file picker) are rejected with a toast so a
   * text-only model never receives an image it would error on. Defaults to
   * `true`.
   */
  imagesSupported?: boolean;
  /**
   * Optional indicator rendered at the start of the trailing control cluster,
   * just before the attach button. Hosts use it to surface a context-usage
   * meter for running agents that report it. A plain `ReactNode` so plugin-chat
   * stays decoupled from how the value is computed.
   */
  contextMeter?: ReactNode;
  /**
   * Bind document-global shortcuts for the toolbar's critical actions —
   * Cmd/Ctrl+. interrupts while generating, Cmd/Ctrl+Alt+U attaches a file,
   * Cmd/Ctrl+Alt+M toggles voice input — and reveal key-cap hints over the
   * toolbar buttons while Alt/Option + Cmd/Ctrl is held. The listener is document-global
   * so the shortcuts work while reading the stream, not just while typing;
   * enable this on at most one composer per page (the primary running-agent
   * surface), never on dialog-hosted launch composers.
   */
  globalHotkeys?: boolean;
}

export function ChatInput({
  onSend,
  onRedirect,
  onStop,
  disabled,
  isGenerating,
  isPending,
  agentActivity,
  devboxShutDown = false,
  compact,
  flush,
  submitLabel,
  autoFocus,
  onEscape,
  initialValue,
  initialAttachments,
  submitRef,
  chatText,
  commands,
  onChange,
  onAttachmentsChange,
  preserveOnSubmit,
  leadingToolbar,
  renderMentionMenu,
  mentionEscapeRef,
  openMentionRef,
  imagesSupported = true,
  contextMeter,
  globalHotkeys = false,
  contextReferences = false,
}: ChatInputProps) {
  const [value, setValue] = useState(initialValue ?? '');
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    onChangeRef.current?.(value);
  }, [value]);

  const onAttachmentsChangeRef = useRef(onAttachmentsChange);
  useEffect(() => {
    onAttachmentsChangeRef.current = onAttachmentsChange;
  }, [onAttachmentsChange]);

  const [interimTranscript, setInterimTranscript] = useState('');
  // Which attached image the viewer is open on, held by attachment id rather
  // than position: attachments finish reading one by one, so an index would
  // point at a different image as the rest of a multi-file drop arrives.
  const [previewId, setPreviewId] = useState<string | null>(null);
  const editorRef = useRef<MentionEditorHandle>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isExternallyDriven = chatText !== undefined;
  const [showCommandPicker, setShowCommandPicker] = useState(false);
  const [commandPickerIndex, setCommandPickerIndex] = useState(0);
  const [pickerStyle, setPickerStyle] = useState<React.CSSProperties>({});

  // Cursor-style `@`-mention menu, active only when the host opts in via
  // `renderMentionMenu`. `mention` holds the active query + caret rect; the menu
  // node and imperative nav handle are owned by the host through the render prop.
  const mentionEnabled = Boolean(renderMentionMenu) && !isExternallyDriven;
  const [mention, setMention] = useState<MentionQuery | null>(null);
  const [mentionStyle, setMentionStyle] = useState<React.CSSProperties>({});
  const mentionMenuRef = useRef<MentionMenuHandle | null>(null);
  // Wraps the portaled menu node so we can detect mousedown events outside it.
  const mentionMenuContainerRef = useRef<HTMLDivElement>(null);
  // True while a menu opened by the external "+" trigger is showing, so a
  // dismiss-without-apply can clean up the bare `@` the trigger inserted.
  const mentionOpenedByButtonRef = useRef(false);
  // After a space-containing query closes with no matches, keep the menu shut
  // for any query that still starts with that failed string until the user
  // backspaces below it.
  const mentionSuppressedPrefixRef = useRef<string | null>(null);
  const mentionEmptyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Query the empty-results timer was started for — LaunchMentionMenu reports
  // on every render, so identical empty reports must not restart the debounce.
  const mentionEmptyTimerQueryRef = useRef<string | null>(null);
  // Untrimmed editor query, read by the empty-results debounce so a trailing
  // space still counts as "has a space" even though the menu receives trimmed.
  const mentionQueryRef = useRef<string | null>(null);

  const speech = useSpeechRecognition({
    onFinalTranscript: (text) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const sep = value.length === 0 || /\s$/.test(value) ? '' : ' ';
      editorRef.current?.insertText(sep + trimmed);
    },
    onInterimTranscript: setInterimTranscript,
  });
  const speechErrorMessage = voiceInputErrorMessage(speech.error);

  const {
    attachments,
    hasUploadsInFlight,
    totalBytes,
    maxTotalBytes,
    isOverLimit,
    isDragOver,
    removeAttachment,
    clearAttachments,
    pasteHandler,
    dragHandlers,
    fileInputProps,
    openFilePicker,
  } = useFileAttachments(initialAttachments, { allowImages: imagesSupported });

  useEffect(() => {
    onAttachmentsChangeRef.current?.(attachments);
  }, [attachments]);

  // Every attached image the composer can show full size, in the order the
  // chips appear. Composer attachments carry their bytes inline, so there is
  // nothing to resolve: an image chip is viewable the moment it exists.
  const viewableImages = useMemo(
    () =>
      attachments.flatMap((att) =>
        att.type === 'image' && att.data && att.mimeType
          ? [{ id: att.id, src: `data:${att.mimeType};base64,${att.data}`, alt: att.name }]
          : [],
      ),
    [attachments],
  );
  const previewIndex = viewableImages.findIndex((image) => image.id === previewId);

  const slashFilter =
    value.startsWith('/') && !value.includes(' ') ? value.slice(1).toLowerCase() : null;
  const filteredCommands = useMemo(
    () =>
      slashFilter !== null && commands?.length ? rankSlashCommands(commands, slashFilter) : [],
    [slashFilter, commands],
  );

  useEffect(() => {
    if (!showCommandPicker || filteredCommands.length === 0) return;
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPickerStyle({
      position: 'fixed',
      bottom: window.innerHeight - rect.top + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 50,
    });
  }, [showCommandPicker, filteredCommands.length]);

  // Anchor the mention menu to the caret rect the editor reports, so it springs
  // from the `@` the user just typed and stays on-screen. Prefer opening above
  // the caret, but flip below when the viewport (or a short mobile sheet) does
  // not leave enough room — and cap height so the list scrolls instead of clipping.
  useEffect(() => {
    if (!mention) return;
    const rect = mention.rect;
    const MENU_WIDTH = 280;
    const VIEWPORT_PADDING = 8;
    const GAP = 4;
    const MIN_MENU_HEIGHT = 160;
    const left = Math.max(
      VIEWPORT_PADDING,
      Math.min(rect.left, window.innerWidth - MENU_WIDTH - VIEWPORT_PADDING),
    );
    const spaceAbove = rect.top - VIEWPORT_PADDING;
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const placeAbove = spaceAbove >= spaceBelow;
    const availableHeight = Math.max(MIN_MENU_HEIGHT, (placeAbove ? spaceAbove : spaceBelow) - GAP);
    setMentionStyle({
      position: 'fixed',
      ...(placeAbove
        ? { bottom: window.innerHeight - rect.top + GAP }
        : { top: rect.bottom + GAP }),
      left,
      width: MENU_WIDTH,
      maxHeight: availableHeight,
      zIndex: 50,
      // The menu is portaled to <body>. A surrounding Radix modal sets
      // `pointer-events: none` on <body>, which the portal inherits and which
      // would swallow clicks on menu rows, so opt the menu back in.
      pointerEvents: 'auto',
    });
  }, [mention]);

  const selectCommand = useCallback(
    (cmd: AvailableCommand) => {
      if (cmd.input) {
        setValue(`/${cmd.name} `);
        editorRef.current?.setText(`/${cmd.name} `);
        setShowCommandPicker(false);
        editorRef.current?.focus();
      } else {
        setValue('');
        editorRef.current?.clear();
        setShowCommandPicker(false);
        onSend(`/${cmd.name}`, []);
      }
    },
    [onSend],
  );

  // The editor reports the active `@`-query (text + caret rect) or null.
  const clearMentionEmptyTimer = useCallback(() => {
    if (mentionEmptyTimerRef.current !== null) {
      clearTimeout(mentionEmptyTimerRef.current);
      mentionEmptyTimerRef.current = null;
    }
    mentionEmptyTimerQueryRef.current = null;
  }, []);

  useEffect(() => () => clearMentionEmptyTimer(), [clearMentionEmptyTimer]);

  const handleMentionQuery = useCallback(
    (active: MentionQuery | null) => {
      if (!mentionEnabled) return;
      // The token going away (backspace, caret leaving it) ends any
      // button-opened session, so a later typed `@` is never mistaken for one.
      if (!active) {
        mentionOpenedByButtonRef.current = false;
        mentionSuppressedPrefixRef.current = null;
        mentionQueryRef.current = null;
        clearMentionEmptyTimer();
        setMention(null);
        return;
      }
      const suppressed = mentionSuppressedPrefixRef.current;
      if (suppressed !== null) {
        if (active.query.startsWith(suppressed)) {
          // Still under the failed multi-word query — keep closed.
          mentionQueryRef.current = active.query;
          setMention(null);
          return;
        }
        // User backspaced below the suppressed prefix — allow reopen.
        mentionSuppressedPrefixRef.current = null;
      }
      mentionQueryRef.current = active.query;
      setMention(active);
    },
    [mentionEnabled, clearMentionEmptyTimer],
  );

  // Serialized editor value drives slash detection and the controlled `value`.
  const handleEditorChange = useCallback(
    (text: string) => {
      setValue(text);
      const isSlash = text.startsWith('/') && !text.includes(' ');
      if (isSlash && commands?.length) {
        setShowCommandPicker(true);
        setCommandPickerIndex(0);
      } else {
        setShowCommandPicker(false);
      }
    },
    [commands],
  );

  // Remove the active `@query` text (a mention configures the run, no inline
  // text). Called by the host after applying persona/attachment results.
  const clearMentionToken = useCallback(() => {
    editorRef.current?.clearQuery();
    mentionQueryRef.current = null;
    clearMentionEmptyTimer();
    setMention(null);
  }, [clearMentionEmptyTimer]);

  // Close the mention menu. When it was opened by the external "+" trigger and
  // no query was typed, also strip the bare `@` the trigger inserted so the
  // button never litters the composer. A typed query is left untouched (the
  // user meant to write it), matching the plain `@`-key behavior.
  const closeMention = useCallback(() => {
    clearMentionEmptyTimer();
    if (mentionOpenedByButtonRef.current && (mention?.query.trim().length ?? 0) === 0) {
      clearMentionToken();
    } else {
      setMention(null);
    }
    mentionOpenedByButtonRef.current = false;
  }, [mention, clearMentionToken, clearMentionEmptyTimer]);

  // Close the menu when the user clicks outside both the composer and the portal'd
  // menu container. `mousedown` fires before `blur`, so we can reliably distinguish
  // a click on a menu item (stays inside mentionMenuContainerRef) from a click
  // elsewhere.
  useEffect(() => {
    if (!mention) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (!(e.target instanceof Node)) return;
      if (wrapperRef.current?.contains(e.target)) return;
      if (mentionMenuContainerRef.current?.contains(e.target)) return;
      closeMention();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [mention, closeMention]);

  // Menu reports emptiness. Debounce-close only space-containing queries so a
  // Suspense flash does not dismiss, and single-word "No matches." stays.
  // Identical empty reports for the same query must not restart the timer —
  // LaunchMentionMenu's ensureSelection runs after every render.
  const reportResults = useCallback(
    (hasResults: boolean) => {
      if (hasResults) {
        clearMentionEmptyTimer();
        return;
      }
      const query = mentionQueryRef.current;
      if (!query || !/\s/.test(query)) {
        clearMentionEmptyTimer();
        return;
      }
      if (mentionEmptyTimerRef.current !== null && mentionEmptyTimerQueryRef.current === query) {
        return;
      }
      clearMentionEmptyTimer();
      mentionEmptyTimerQueryRef.current = query;
      mentionEmptyTimerRef.current = setTimeout(() => {
        mentionEmptyTimerRef.current = null;
        mentionEmptyTimerQueryRef.current = null;
        // Set suppression before closing so a later editor report for the same
        // token does not reopen the menu.
        mentionSuppressedPrefixRef.current = query;
        closeMention();
      }, MENTION_EMPTY_CLOSE_MS);
    },
    [clearMentionEmptyTimer, closeMention],
  );

  // Replace the active `@query` with an inline, atomic context chip.
  const insertMention = useCallback((mention: MentionData) => {
    editorRef.current?.insertMentionAtQuery(mention);
    setMention(null);
  }, []);

  // Clear the text after `@` but keep the menu open (category drill in/out).
  const resetMentionQuery = useCallback(() => {
    editorRef.current?.resetQuery();
  }, []);

  // Rewrite the text after `@` (e.g. to `repo:`) to scope the menu to a
  // category; the menu stays open and continues searching from the new text.
  const setMentionQuery = useCallback((text: string) => {
    editorRef.current?.setQuery(text);
  }, []);

  // Ladder Escape through the open mention menu: let the menu step back one
  // level (sub-category -> root); when it has nowhere left to go, close the
  // menu. Returns true when the menu consumed the key, so a host inside a Radix
  // dismissable layer can `preventDefault()` and keep itself open. Shared by the
  // editor's keydown handler (no surrounding dialog) and `mentionEscapeRef` (the
  // dialog path, which runs first because Radix listens on `document`/capture).
  const handleMentionEscape = useCallback((): boolean => {
    if (!(mentionEnabled && mention)) return false;
    if (!mentionMenuRef.current?.escape?.()) closeMention();
    return true;
  }, [mentionEnabled, mention, closeMention]);

  useEffect(() => {
    if (!mentionEscapeRef) return;
    mentionEscapeRef.current = handleMentionEscape;
    return () => {
      mentionEscapeRef.current = null;
    };
  }, [mentionEscapeRef, handleMentionEscape]);

  // Let a host "+" trigger open the same menu the `@` key does.
  useEffect(() => {
    if (!openMentionRef) return;
    openMentionRef.current = () => {
      if (!mentionEnabled) return;
      // Only an `@` the trigger itself inserted is cleaned up on dismiss. When
      // the caret already sat in an `@`-token the editor reuses it, and that
      // character is the user's to keep. Accumulated rather than assigned: a
      // second click reuses the token the first click inserted, and that `@`
      // is still ours to clean up. `handleMentionQuery` clears the flag as soon
      // as the token goes away, so a lingering `true` is always the trigger's.
      const inserted = editorRef.current?.openMentionMenu() ?? false;
      mentionOpenedByButtonRef.current ||= inserted;
    };
    return () => {
      openMentionRef.current = null;
    };
  }, [openMentionRef, mentionEnabled]);

  const displayValue = isExternallyDriven ? (chatText ?? '') : value;
  const hasContent = displayValue.trim().length > 0 || attachments.length > 0;
  useBlockRefresh('chat-input', hasContent || hasUploadsInFlight);

  // Replay mode is host-controlled: mirror `chatText` into the editor.
  useEffect(() => {
    if (isExternallyDriven) editorRef.current?.setText(chatText ?? '');
  }, [isExternallyDriven, chatText]);

  // Shared submit routine. `dispatch` decides where the payload goes: the
  // regular `onSend` (queues while the agent is working) or `onRedirect`
  // (interrupts the turn and sends now). Both share the same validation and
  // post-submit cleanup.
  const submitWith = useCallback(
    (dispatch: (message: string, attachments: Attachment[]) => void) => {
      // Externally driven inputs (e.g. interactive replay) have no internal
      // value — the prefilled message lives on `chatText`. Use that as the
      // payload so clicking Send with a prefill actually sends the original
      // replay message.
      const source = isExternallyDriven ? (chatText ?? '') : value;
      const trimmed = source.trim();
      if (!trimmed && attachments.length === 0) return;
      if (hasUploadsInFlight || isPending) return;
      if (isOverLimit) return;
      if (speech.listening) speech.stop();
      dispatch(trimmed, attachments);
      // Close any open mention menu so it can't linger over a submitted composer.
      setMention(null);
      if (!isExternallyDriven && !preserveOnSubmit) {
        setValue('');
        editorRef.current?.clear();
        setInterimTranscript('');
        clearAttachments();
      }
    },
    [
      isExternallyDriven,
      chatText,
      value,
      attachments,
      hasUploadsInFlight,
      isPending,
      isOverLimit,
      clearAttachments,
      speech,
      preserveOnSubmit,
    ],
  );

  const handleSubmit = useCallback(() => submitWith(onSend), [submitWith, onSend]);

  // Redirect is only meaningful while the agent is generating and the host
  // opted in with `onRedirect`.
  const canRedirect = Boolean(onRedirect) && Boolean(isGenerating);
  const handleRedirect = useCallback(() => {
    if (onRedirect) submitWith(onRedirect);
  }, [submitWith, onRedirect]);

  useEffect(() => {
    if (submitRef) submitRef.current = handleSubmit;
  }, [submitRef, handleSubmit]);

  // Capture-phase keydown for the editor. Returns `true` when the host consumed
  // the key (the editor then prevents default + stops propagation, so it never
  // reaches Lexical or the Dialog's Escape listener). Returns `false` for keys
  // the composer has nothing to do with — most importantly Escape when no
  // `onEscape` is set and no turn is generating — so a wrapping surface (the
  // Labs takeover's `esc`-back, a Radix Dialog close) can still act on it.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>): boolean => {
      // Mention menu open: drive its selection. Enter/Tab only consume when an
      // item was applied; otherwise they fall through to submit/newline.
      if (mentionEnabled && mention) {
        if (e.key === 'ArrowDown') {
          mentionMenuRef.current?.moveDown();
          return true;
        }
        if (e.key === 'ArrowUp') {
          mentionMenuRef.current?.moveUp();
          return true;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
          if (mentionMenuRef.current?.selectHighlighted()) return true;
        } else if (e.key === 'Tab') {
          if (mentionMenuRef.current?.selectHighlighted()) return true;
        } else if (e.key === 'Escape') {
          // Step back through the menu (sub-category -> root -> close). When no
          // dialog wraps the composer, this is the only Escape handler.
          handleMentionEscape();
          return true;
        } else if (e.key === 'Backspace' && /^[^\s:]+:$/.test(mention.query)) {
          // The active token is a bare category scope (`@<keyword>:` with an
          // empty inner query). Deleting the trailing `:` clears the whole
          // keyword back to a lone `@` (menu stays open on the category list),
          // instead of dropping into a `@<keyword>` query one char at a time.
          resetMentionQuery();
          return true;
        }
      }

      if (showCommandPicker && filteredCommands.length > 0) {
        if (e.key === 'ArrowDown') {
          setCommandPickerIndex((i) => Math.min(i + 1, filteredCommands.length - 1));
          return true;
        }
        if (e.key === 'ArrowUp') {
          setCommandPickerIndex((i) => Math.max(i - 1, 0));
          return true;
        }
        if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
          selectCommand(filteredCommands[commandPickerIndex]);
          return true;
        }
        if (e.key === 'Escape') {
          setShowCommandPicker(false);
          return true;
        }
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        const canSubmit = hasContent && !hasUploadsInFlight && !isOverLimit;
        // Cmd/Ctrl+Enter redirects: interrupt the current turn and send now,
        // rather than plain Enter which queues behind the in-flight turn.
        if ((e.metaKey || e.ctrlKey) && canRedirect && canSubmit) {
          handleRedirect();
        } else if (isGenerating && !hasContent) {
          onStop?.();
        } else if (canSubmit) {
          handleSubmit();
        }
        return true;
      }
      if (e.key === 'Escape') {
        // Escape is only "ours" when there's something to do with it: stop a
        // generating turn, or hand it to the host's `onEscape`. Otherwise it
        // must fall through so a wrapping surface (the Labs takeover's `esc`
        // back, a Radix Dialog close) can act on it. Swallowing it here would
        // strand the user inside the composer with no keyboard way out.
        if (isGenerating) {
          onStop?.();
          return true;
        }
        if (onEscape) {
          onEscape();
          return true;
        }
        return false;
      }
      return false;
    },
    [
      handleSubmit,
      handleRedirect,
      canRedirect,
      isGenerating,
      hasContent,
      hasUploadsInFlight,
      isOverLimit,
      onStop,
      onEscape,
      showCommandPicker,
      filteredCommands,
      commandPickerIndex,
      selectCommand,
      mentionEnabled,
      mention,
      handleMentionEscape,
      resetMentionQuery,
    ],
  );

  // Clicking the composer's chrome should drop the caret into the editor — but
  // only chrome that is not itself a focus target. A click on a tab stop
  // belongs to that tab stop: `MentionEditor.focus()` goes through Lexical,
  // which lands the caret asynchronously, so it arrives *after* a toolbar
  // control's popover has opened and focused itself, and Radix reads the late
  // focus as focus-outside and closes the popover on the spot.
  //
  // The `contains` check drops clicks that never happened in this DOM subtree:
  // a toolbar control's popover is portaled out of the composer while its
  // events still bubble up the React tree to here, and refocusing on those
  // stole focus from the popover's own inputs (holding the mouse down was the
  // only way to type into the inline "Add a key" form).
  const handleContainerClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof Element) || !event.currentTarget.contains(target)) return;
    if (target.closest(FOCUSABLE_SELECTOR)) return;
    editorRef.current?.focus();
  }, []);

  const showStop = isGenerating && !hasContent;
  const showSend = !isGenerating || hasContent;
  const effectiveDisabled = disabled || isPending;

  // Hold Alt/Option + Cmd/Ctrl to reveal key-cap hints over the toolbar
  // buttons. Each hint only shows while its control is actually usable, so the
  // reveal never advertises a dead shortcut.
  const hotkeyHintsShown = useAltKeyHeld(globalHotkeys) && !isExternallyDriven;
  const sendReady = !effectiveDisabled && !hasUploadsInFlight && !isOverLimit;

  // Latest-ref for the global shortcut actions: the listener stays bound once
  // while each action reflects the current render (an action is undefined
  // when its control is unavailable, so the shortcut is inert exactly when
  // the button is).
  const hotkeyActionsRef = useRef<{
    interrupt?: () => void;
    attach?: () => void;
    mic?: () => void;
  }>({});
  useEffect(() => {
    hotkeyActionsRef.current = {
      interrupt: isGenerating && !isExternallyDriven ? onStop : undefined,
      attach: !effectiveDisabled && !isExternallyDriven ? openFilePicker : undefined,
      mic:
        !effectiveDisabled && !isExternallyDriven && speech.supported ? speech.toggle : undefined,
    };
  });

  useEffect(() => {
    if (!globalHotkeys) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      // Auto-repeat would spam the interrupt call or churn speech sessions,
      // and Ctrl+. doubles as a punctuation-width toggle in some IMEs.
      if (e.repeat || e.isComposing) return;
      // Don't hijack keys while focus lives in a modal layer above the
      // composer: the launch dialog over the agents page, a delete-agent
      // confirm (role="alertdialog"), or this composer's own image preview.
      if (e.target instanceof Element && e.target.closest('[role="dialog"], [role="alertdialog"]'))
        return;
      // Cmd/Ctrl+. — the classic macOS cancel key — interrupts the turn.
      // Shift is tolerated because '.' needs Shift on some layouts (AZERTY).
      if (e.key === '.' && !e.altKey) {
        const { interrupt } = hotkeyActionsRef.current;
        if (interrupt) {
          e.preventDefault();
          interrupt();
        }
        return;
      }
      if (!e.altKey || e.shiftKey) return;
      // Match by produced letter, not physical code: the tooltips promise
      // "Alt+U"/"Alt+M", and on non-QWERTY layouts (AZERTY, Dvorak) the
      // physical KeyU/KeyM positions carry different letters.
      const letter = e.key.toUpperCase();
      if (letter === 'U') {
        const { attach } = hotkeyActionsRef.current;
        if (attach) {
          e.preventDefault();
          attach();
        }
      } else if (letter === 'M') {
        const { mic } = hotkeyActionsRef.current;
        if (mic) {
          e.preventDefault();
          mic();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [globalHotkeys]);

  const placeholder = getChatInputPlaceholder({
    isExternallyDriven,
    devboxShutDown,
    agentActivity,
    isGenerating,
    canRedirect,
  });

  return (
    <div
      ref={wrapperRef}
      className={cn(
        compact ? 'mb-1' : 'mb-2 md:mb-3',
        // A compact composer is always hosted by a surface that owns its own
        // gutter, so it implies `flush`.
        !compact && !flush && 'mx-2 md:mx-4',
      )}
    >
      {showCommandPicker &&
        filteredCommands.length > 0 &&
        createPortal(
          <CommandPicker
            commands={filteredCommands}
            selectedIndex={commandPickerIndex}
            onSelect={selectCommand}
            onHover={setCommandPickerIndex}
            style={pickerStyle}
          />,
          document.body,
        )}
      {mentionEnabled &&
        mention &&
        renderMentionMenu &&
        createPortal(
          <div ref={mentionMenuContainerRef}>
            {renderMentionMenu({
              query: mention.query.trim(),
              style: mentionStyle,
              onClose: closeMention,
              clearMentionToken,
              insertMention,
              resetMentionQuery,
              setMentionQuery,
              reportResults,
              menuRef: mentionMenuRef,
            })}
          </div>,
          document.body,
        )}
      <div
        role="form"
        aria-label="Message input"
        data-tutorial-id="agent-chat-input"
        onClick={handleContainerClick}
        {...dragHandlers}
        className={cn(
          'gradient-focus-ring relative flex cursor-text flex-col gap-1 rounded-xl border bg-card/60 px-3 py-2 backdrop-blur-md',
          isDragOver ? 'border-ring/60 bg-accent/30' : 'border-border/40',
          agentActivity === 'waiting_input' &&
            'border-status-warning/50 ring-1 ring-status-warning/20',
        )}
      >
        {attachments.length > 0 && (
          <div className="flex items-start justify-between gap-2 pb-1">
            <div className="flex min-w-0 flex-1 flex-wrap gap-1.5" data-testid="attachment-list">
              {attachments.map((att) => (
                <AttachmentChip
                  key={att.id}
                  attachment={att}
                  onRemove={() => removeAttachment(att.id)}
                  onClick={
                    viewableImages.some((image) => image.id === att.id)
                      ? () => setPreviewId(att.id)
                      : undefined
                  }
                />
              ))}
            </div>
            <div
              data-testid="attachment-total"
              data-over-limit={isOverLimit || undefined}
              className={cn(
                'shrink-0 text-right text-2xs',
                isOverLimit ? 'font-medium text-destructive' : 'text-muted-foreground',
              )}
            >
              {isOverLimit
                ? `${formatBytes(totalBytes)} of ${formatBytes(maxTotalBytes)} limit - remove files to send`
                : `${formatBytes(totalBytes)} / ${formatBytes(maxTotalBytes)}`}
            </div>
          </div>
        )}

        <MentionEditor
          ref={editorRef}
          initialText={isExternallyDriven ? undefined : initialValue}
          editable={
            !isExternallyDriven &&
            !(
              effectiveDisabled ||
              devboxShutDown ||
              agentActivity === 'stopped' ||
              agentActivity === 'error'
            )
          }
          autoFocus={!isExternallyDriven && autoFocus}
          onChange={isExternallyDriven ? undefined : handleEditorChange}
          onMentionQueryChange={mentionEnabled ? handleMentionQuery : undefined}
          onKeyDownCapture={isExternallyDriven ? undefined : handleKeyDown}
          onPaste={isExternallyDriven ? undefined : pasteHandler}
          contextUrls={contextReferences}
          placeholder={placeholder}
          className={cn(
            // 16px on phones: iOS Safari auto-zooms the page when a focused
            // editable is smaller, which breaks the layout around the composer.
            'overflow-y-auto text-base sm:text-sm',
            compact ? 'min-h-[72px] max-h-[240px]' : 'min-h-[36px] max-h-[150px]',
          )}
        />

        {!isExternallyDriven && speech.listening && (
          <div
            data-testid="speech-interim"
            className="pointer-events-none pb-1 text-xs italic text-muted-foreground"
            aria-live="polite"
          >
            {interimTranscript || 'Listening…'}
          </div>
        )}

        {/* Voice input can fail in ways the user can fix (a blocked mic, a
            missing provider key on the server-side fallback), and the mic
            button alone gives no hint. Routine codes are filtered out by
            `voiceInputErrorMessage`, so this only appears when acting on it
            would help. */}
        {!isExternallyDriven && !speech.listening && speechErrorMessage && (
          <div
            data-testid="speech-error"
            className="pointer-events-none pb-1 text-xs text-destructive"
            role="status"
          >
            {speechErrorMessage}
          </div>
        )}

        {!isExternallyDriven && (
          <div className="flex items-center justify-between gap-2">
            {/* The leading toolbar (settings bar) always shares this row with the
                action icons: it stays `min-w-0` so its segments (agent name,
                model name) shrink and truncate to whatever width is left rather
                than wrapping onto their own line. The action icons never shrink
                (`shrink-0`), so they stay fully visible on the narrowest phone. */}
            <div className="-ml-1 flex min-w-0 items-center gap-1.5">{leadingToolbar}</div>
            {/* gap-2 (vs gap-1) leaves enough room between the small action
                buttons that their hold-Alt key-cap hints, which pop over each
                button's top-right corner, stay separated instead of overlapping
                a neighbour. */}
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {contextMeter}
              <HotkeyHint keys={['mod', 'alt', 'U']} show={hotkeyHintsShown && !effectiveDisabled}>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  disabled={effectiveDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    openFilePicker();
                  }}
                  aria-label="Attach file"
                  aria-keyshortcuts={globalHotkeys ? 'Control+Alt+U Meta+Alt+U' : undefined}
                  title={
                    (imagesSupported
                      ? 'Attach file'
                      : "Attach file (the selected model can't read images)") +
                    (globalHotkeys ? ' (Cmd/Ctrl+Alt+U)' : '')
                  }
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
              </HotkeyHint>
              <input {...fileInputProps} />
              {speech.supported && (
                <HotkeyHint
                  keys={['mod', 'alt', 'M']}
                  show={hotkeyHintsShown && !effectiveDisabled}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'h-7 w-7',
                      speech.listening
                        ? 'animate-pulse text-destructive hover:text-destructive'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                    disabled={effectiveDisabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      speech.toggle();
                    }}
                    aria-label={speech.listening ? 'Stop voice input' : 'Start voice input'}
                    aria-pressed={speech.listening}
                    aria-keyshortcuts={globalHotkeys ? 'Control+Alt+M Meta+Alt+M' : undefined}
                    title={globalHotkeys ? 'Voice input (Cmd/Ctrl+Alt+M)' : undefined}
                  >
                    {speech.listening ? (
                      <MicOff className="h-4 w-4" />
                    ) : (
                      <Mic className="h-4 w-4" />
                    )}
                  </Button>
                </HotkeyHint>
              )}
              {showStop && (
                <HotkeyHint keys={['mod', '.']} show={hotkeyHintsShown}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="secondary"
                        size="icon"
                        className="h-7 w-7"
                        onClick={(e) => {
                          e.stopPropagation();
                          onStop?.();
                        }}
                        aria-label="Interrupt agent"
                        aria-keyshortcuts={globalHotkeys ? 'Control+. Meta+.' : undefined}
                        data-replay-target="interrupt"
                      >
                        <Square className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {globalHotkeys ? 'Interrupt agent (Cmd/Ctrl+.)' : 'Interrupt agent'}
                    </TooltipContent>
                  </Tooltip>
                </HotkeyHint>
              )}
              {showSend &&
                (canRedirect && hasContent ? (
                  // While the agent is working, queueing is the primary action:
                  // it is rightmost to match the usual send affordance and keeps
                  // the current turn intact. Redirect remains available to its
                  // left as the deliberate interrupt-and-send-now alternative.
                  <>
                    <HotkeyHint keys={['mod', 'enter']} show={hotkeyHintsShown && sendReady}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon"
                            className="h-7 w-7"
                            disabled={effectiveDisabled || hasUploadsInFlight || isOverLimit}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRedirect();
                            }}
                            aria-label="Redirect agent"
                            data-replay-target="redirect"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          Redirect agent: interrupt and send now (Cmd/Ctrl+Enter)
                        </TooltipContent>
                      </Tooltip>
                    </HotkeyHint>
                    <HotkeyHint keys={['enter']} show={hotkeyHintsShown && sendReady}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="default"
                            size="icon"
                            className="h-7 w-7"
                            disabled={effectiveDisabled || hasUploadsInFlight || isOverLimit}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSubmit();
                            }}
                            aria-label="Queue message"
                            data-replay-target="send"
                          >
                            {isPending ? <Spinner size="sm" /> : <ListPlus className="h-4 w-4" />}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Queue message (Enter)</TooltipContent>
                      </Tooltip>
                    </HotkeyHint>
                  </>
                ) : (
                  <HotkeyHint keys={['enter']} show={hotkeyHintsShown && sendReady && hasContent}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant={compact ? 'default' : 'secondary'}
                          size="icon"
                          className="h-7 w-7"
                          disabled={
                            effectiveDisabled || !hasContent || hasUploadsInFlight || isOverLimit
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubmit();
                          }}
                          aria-label={submitLabel ?? 'Send message'}
                          data-replay-target="send"
                        >
                          {isPending ? <Spinner size="sm" /> : <ArrowUp className="h-4 w-4" />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{submitLabel ?? 'Send message'}</TooltipContent>
                    </Tooltip>
                  </HotkeyHint>
                ))}
            </div>
          </div>
        )}

        {viewableImages.length > 0 && (
          // Kept mounted rather than gated on `previewIndex`, so Radix still
          // sees the open-to-closed transition and plays the dialog's fade-out
          // instead of ripping it off the screen. Once it is closing there is
          // no open image to index, so the initial index falls back to the
          // first — the effect that reads it only runs while open.
          <ImageLightbox
            images={viewableImages}
            initialIndex={Math.max(previewIndex, 0)}
            open={previewIndex !== -1}
            onOpenChange={(open) => {
              if (!open) setPreviewId(null);
            }}
            // A draft attachment has not been sent, so the agent has no image
            // to be told about a spot in. Whatever the surrounding chat offers,
            // the composer's own viewer stays a plain viewer.
            commentable={false}
          />
        )}

        {isExternallyDriven && (hasContent || leadingToolbar) && (
          <div className="flex items-center justify-between gap-2">
            <div className="-ml-1 flex min-w-0 items-center gap-1.5">{leadingToolbar}</div>
            {hasContent && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant={compact ? 'default' : 'secondary'}
                    size="icon"
                    className="ml-auto h-7 w-7 shrink-0"
                    disabled={effectiveDisabled || hasUploadsInFlight || isOverLimit}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSubmit();
                    }}
                    aria-label={submitLabel ?? 'Send message'}
                    data-replay-target="send"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{submitLabel ?? 'Send message'}</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
